import { THEMES, WORLD_H, WORLD_W, generateMap, surfaceY, type ThemeId } from '@cuboom/shared';

/**
 * Mini map preview for the setup picker: a seeded generateMap run rendered
 * as a palette-true silhouette (sky, hills, terrain profile, water, prop
 * dots) so players see the map before committing. DOM-safe; shares the sim's
 * generator, so what you preview is exactly what will play.
 */

/** Fixed seed — the preview for a theme is stable across openings. */
export const PREVIEW_SEED = 7;

// Last painted combo — repaints (selection highlight changes) reuse the map.
let cached: { id: ThemeId; seed: number; gen: ReturnType<typeof generateMap> } | null = null;

function scaledMap(seed: number, themeId: ThemeId): ReturnType<typeof generateMap> {
  if (cached && cached.id === themeId && cached.seed === seed) return cached.gen;
  const gen = generateMap(seed, themeId, [3, 3]);
  cached = { id: themeId, seed, gen };
  return gen;
}

export function paintMapPreview(
  canvas: HTMLCanvasElement,
  themeId: ThemeId,
  seed: number = PREVIEW_SEED,
): void {
  const theme = THEMES[themeId];
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cssW = canvas.clientWidth || 300;
  const cssH = canvas.clientHeight || 130;
  const bw = Math.max(1, Math.round(cssW * dpr));
  const bh = Math.max(1, Math.round(cssH * dpr));
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw;
    canvas.height = bh;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Sky.
  const sky = ctx.createLinearGradient(0, 0, 0, cssH);
  sky.addColorStop(0, theme.skyTop);
  sky.addColorStop(1, theme.skyBottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, cssW, cssH);

  // Two hill bands (same shape family as the real backdrop).
  const band = (color: string, frac: number, amp: number, freq: number, phase: number): void => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, cssH);
    for (let sx = 0; sx <= cssW; sx += 4) {
      const y = cssH * frac + Math.sin(sx * freq + phase) * amp + Math.sin(sx * freq * 2.7 + phase * 2) * amp * 0.4;
      ctx.lineTo(sx, y);
    }
    ctx.lineTo(cssW, cssH);
    ctx.closePath();
    ctx.fill();
  };
  band(theme.hillBack, 0.5, 8, 0.05, 3.1);
  band(theme.hillFront, 0.62, 6, 0.09, 1.7);

  const map = scaledMap(seed, themeId);

  // Terrain silhouette.
  const sx = (wx: number): number => (wx / WORLD_W) * cssW;
  const sy = (wy: number): number => (wy / WORLD_H) * cssH;
  ctx.beginPath();
  ctx.moveTo(0, cssH);
  for (let px = 0; px <= cssW; px += 2) {
    const wx = (px / cssW) * WORLD_W;
    const s = surfaceY(map.terrain, wx);
    ctx.lineTo(px, s < 0 ? cssH : sy(s));
  }
  ctx.lineTo(cssW, cssH);
  ctx.closePath();
  const dirtG = ctx.createLinearGradient(0, cssH * 0.35, 0, cssH);
  dirtG.addColorStop(0, theme.grass);
  dirtG.addColorStop(0.22, theme.grassDark);
  dirtG.addColorStop(1, theme.dirtDark);
  ctx.fillStyle = dirtG;
  ctx.fill();

  // Water pools.
  ctx.fillStyle = `${theme.water}cc`;
  for (const w of map.water) {
    ctx.fillRect(sx(w.x), sy(w.y), Math.max(2, sx(w.w)), cssH - sy(w.y));
  }

  // Prop dots — enough to show density and palette, not a full render.
  for (const p of map.props.slice(0, 60)) {
    const color =
      p.kind === 'flower'
        ? `hsl(${Math.floor(p.style * 360)}, 70%, 60%)`
        : p.kind === 'crystal'
          ? `hsl(${Math.floor(p.style * 360)}, 75%, 65%)`
          : p.kind === 'rock'
            ? '#9aa0a8'
            : p.kind === 'lamp'
              ? '#ffd76a'
              : p.kind === 'pot'
                ? '#b35a37'
                : theme.grassDark;
    ctx.fillStyle = color;
    const r = Math.max(1.4, sx(p.w) * 0.16);
    ctx.beginPath();
    ctx.arc(sx(p.x), sy(p.y) - r, r, 0, Math.PI * 2);
    ctx.fill();
  }
}
